import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormHelperText,
  Switch,
  TextField,
  Button,
} from '@mui/material';
import { AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { OpsItemStrategy } from '../../../types/backend';
import type { OpsItemStrategyType, ResolveSignal } from '../../../types/backend';
import { detectResolveSignal } from '../utils/ruleFormUtils';
import { useCustomerService } from '../../customer/services/customerService';
import type { ItsmEntityRef } from '../../../types/backend';
import DedupFieldsEditor from './DedupFieldsEditor';
import FieldMappingInput from './targets/FieldMappingInput';

/** Status an auto-resolve drives the ticket to when the rule does not name one. */
const DEFAULT_RESOLVE_STATUS = 'resolved';
/**
 * Canonical status names to the ITSM's FIXED status codes. The adapter resolves an exact
 * canonical spelling straight to these without consulting the tenant's labels, so the
 * picker has to follow the same route or it shows a destination the ticket will not reach
 * on a tenant that renamed its defaults. The codes are a vendor constant, not tenant data.
 */
const CANONICAL_STATUS_CODES: Record<string, number> = {
  open: 2, pending: 3, resolved: 4, closed: 5,
};

interface IncidentLifecycleSectionProps {
  customerId: string;
  opsitemStrategy: OpsItemStrategyType;
  dedupFieldPaths: string[];
  resolveSignal: ResolveSignal | null;
  sampleEvent: Record<string, unknown>;
  onStrategyChange: (strategy: OpsItemStrategyType) => void;
  onDedupChange: (paths: string[]) => void;
  onResolveChange: (signal: ResolveSignal | null) => void;
  hasAttemptedSave: boolean;
  validationErrors: Record<string, string>;
  disabled?: boolean;
}

/**
 * Step ⑤ — the incident lifecycle: how repeat firings group into incidents, and
 * how an incident auto-resolves when a recovery event arrives. Auto-resolve needs
 * a rolling incident, so it is only offered for track / fingerprint strategies.
 */
const IncidentLifecycleSection: React.FC<IncidentLifecycleSectionProps> = ({
  customerId,
  opsitemStrategy,
  dedupFieldPaths,
  resolveSignal,
  sampleEvent,
  onStrategyChange,
  onDedupChange,
  onResolveChange,
  hasAttemptedSave,
  validationErrors,
  disabled,
}) => {
  const customerService = useCustomerService();
  const groups = opsitemStrategy !== OpsItemStrategy.ALWAYS_NEW;
  const resolveEnabled = resolveSignal !== null;

  // The tenant's own status vocabulary — Freshservice statuses are per-tenant, so the
  // choices are fetched rather than hardcoded. Only fetched once auto-resolve is on:
  // most rules never resolve themselves and should not pay for the lookup.
  // Through the query layer (FE-13), which is also what keeps a previous tenant's list
  // from ever being selectable: data is keyed per customer, so switching customer cannot
  // show customer A's statuses under B — no hand-rolled stamping, no stale frame.
  // `enabled` keeps the Freshservice round trip off the majority of rules, which never
  // auto-resolve; a re-toggle inside staleTime is served from cache rather than refetched.
  const statusQuery = useQuery<ItsmEntityRef[]>({
    queryKey: ['customers', 'freshservice-statuses', customerId],
    queryFn: () => customerService.listFreshserviceStatuses(customerId),
    enabled: resolveEnabled && !!customerId,
    // No staleTime: re-opening this section revalidates rather than trusting a list the
    // tenant may have changed since. Cached options stay on screen while it refetches.
    staleTime: 0,
  });
  const statuses = statusQuery.data ?? [];
  // A refetch failure keeps the previously loaded options, so only claim the saved value
  // is all we have when that is actually true.
  const statusesFailed = statusQuery.isError;
  const statusesEmpty = statusesFailed && statuses.length === 0;

  // Match the saved status to the tenant's own spelling, case-insensitively: the
  // default is the canonical lowercase 'resolved' while Freshservice labels it
  // 'Resolved', and the backend resolves either. When it matches nothing — the lookup
  // failed, or the tenant dropped the status — the saved value is prepended so it stays
  // visible and selected; a blank select would silently rewrite the rule on next save.
  const savedStatus = resolveSignal?.ticket_status ?? DEFAULT_RESOLVE_STATUS;
  // The adapter's precedence, in the adapter's order, because a mismatch here shows the
  // operator one destination while the recovery writes another. An exact canonical spelling
  // resolves by CODE, never by label: on a tenant that renamed its defaults, matching the
  // stored 'resolved' against a label reading "Resolved" would point at a different status
  // than the ticket lands in, and re-picking it would store that label and silently
  // retarget the rule. Tenant labels win only over a case-folded canonical match.
  // Exact lowercase only, because that is the whole of the adapter's canonical fast path:
  // it tests membership of a lowercase-keyed map case-sensitively. Any other spelling,
  // including a tenant label that happens to read "Resolved", goes to tenant labels first
  // and can land on a different code, so folding case here would show the code-4 status
  // while the recovery writes the label's own.
  const canonicalCode = CANONICAL_STATUS_CODES[savedStatus.trim()];
  const matchedStatus = canonicalCode !== undefined
    ? statuses.find((s) => s.id === canonicalCode)
    : statuses.find((s) => s.name.trim() === savedStatus.trim())
      ?? statuses.find((s) => s.name.trim().toLowerCase() === savedStatus.trim().toLowerCase());
  const selectedStatus = matchedStatus?.name ?? savedStatus;
  // JS has no casefold(), so a pair of labels that differ only by case-folding can still
  // display more optimistically than the backend matches. That stays cosmetic: the stored
  // value is shown as-is when nothing matches and is never rewritten by rendering.
  const statusOptions = matchedStatus ? statuses : [{ id: -1, name: savedStatus }, ...statuses];

  const handleStrategyChange = (next: OpsItemStrategyType) => {
    // Auto-resolve is meaningless without a rolling incident to close.
    if (next === OpsItemStrategy.ALWAYS_NEW && resolveSignal !== null) {
      onResolveChange(null);
    }
    onStrategyChange(next);
  };

  const toggleResolve = (on: boolean) => {
    if (!on) {
      onResolveChange(null);
      return;
    }
    // Prefill from the sample event when it looks like an alarm; else start blank.
    onResolveChange(detectResolveSignal(sampleEvent) ?? { field_path: '', values: [] });
  };

  const patchResolve = (changes: Partial<ResolveSignal>) => {
    if (!resolveSignal) return;
    onResolveChange({ ...resolveSignal, ...changes });
  };

  const suggestion = detectResolveSignal(sampleEvent);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Grouping */}
      <FormControl size="small" fullWidth>
        <InputLabel shrink>Grouping</InputLabel>
        <Select
          value={opsitemStrategy}
          onChange={(e) => handleStrategyChange(e.target.value as OpsItemStrategyType)}
          label="Grouping"
          notched
          disabled={disabled}
        >
          <MenuItem value={OpsItemStrategy.TRACK}>One rolling action log item</MenuItem>
          <MenuItem value={OpsItemStrategy.ALWAYS_NEW}>New action log item every time</MenuItem>
          <MenuItem value={OpsItemStrategy.FINGERPRINT}>One per matching fields</MenuItem>
        </Select>
      </FormControl>

      {opsitemStrategy === OpsItemStrategy.FINGERPRINT && (
        <DedupFieldsEditor
          paths={dedupFieldPaths}
          onChange={onDedupChange}
          sampleEvent={sampleEvent}
          error={hasAttemptedSave ? validationErrors.dedup_field_paths : undefined}
          disabled={disabled}
        />
      )}

      {/* Resolve */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
        {!groups ? (
          <Typography variant="caption" color="text.secondary">
            Auto-resolve needs a rolling action log item — use Track or Fingerprint grouping.
          </Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={resolveEnabled}
                  onChange={(e) => toggleResolve(e.target.checked)}
                  size="small"
                  disabled={disabled}
                />
              }
              label={<Typography variant="body2" fontWeight={600}>Auto-resolve on recovery</Typography>}
            />

            {resolveEnabled && resolveSignal && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Resolve the action log item and move the ticket when a matching event arrives.
                </Typography>

                {suggestion && resolveSignal.field_path !== suggestion.field_path && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => patchResolve({
                      field_path: suggestion.field_path,
                      values: suggestion.values,
                    })}
                    disabled={disabled}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Suggest: {suggestion.field_path} = {suggestion.values[0]}
                  </Button>
                )}

                <FieldMappingInput
                  label="Field"
                  value={resolveSignal.field_path}
                  onChange={(v) => patchResolve({ field_path: v })}
                  sampleEvent={sampleEvent}
                  disabled={disabled}
                />
                {hasAttemptedSave && validationErrors.resolve_field_path && (
                  <Typography variant="caption" color="error">{validationErrors.resolve_field_path}</Typography>
                )}

                <TextField
                  fullWidth
                  size="small"
                  label="Equals (any of)"
                  value={resolveSignal.values.join(', ')}
                  onChange={(e) =>
                    patchResolve({
                      values: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  placeholder="OK"
                  error={hasAttemptedSave && !!validationErrors.resolve_values}
                  helperText={(hasAttemptedSave && validationErrors.resolve_values) || 'Comma-separated'}
                  disabled={disabled}
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel shrink id="resolve-ticket-status-label">Ticket status</InputLabel>
                  <Select
                    labelId="resolve-ticket-status-label"
                    value={selectedStatus}
                    onChange={(e) => patchResolve({ ticket_status: e.target.value })}
                    label="Ticket status"
                    notched
                    disabled={disabled}
                  >
                    {statusOptions.map((s) => (
                      <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {statusesEmpty
                      ? 'Could not load this customer\u2019s Freshservice statuses \u2014 showing the saved value only.'
                      : statusesFailed
                        ? 'Could not refresh this customer\u2019s Freshservice statuses \u2014 these may be out of date.'
                        : 'The status the ticket is moved to on recovery'}
                  </FormHelperText>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Resolution note"
                  value={resolveSignal.resolution ?? ''}
                  onChange={(e) => patchResolve({ resolution: e.target.value || undefined })}
                  placeholder="Alarm cleared"
                  helperText="Optional — shown on the ticket"
                  disabled={disabled}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default IncidentLifecycleSection;
